import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import {
  Alert,
  Box,
  Chip,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import {
  capabilityLabelKey,
  domainLabelKey,
  isNoAccess,
  levelLabelKey,
} from '@/shared/access';
import type { AccessLevel, CapabilityReason, GrantSource, ResolvedDomain } from '@/shared/access';
import { useEmployees } from '@/features/employees';
import { usePermissions } from '@/shared/permissions';
import { EmptyState, ErrorState, LoadingState } from '@/shared/ui';

import { useEffectiveAccess, useMyEffectiveAccess } from '../../hooks';

const LEVEL_COLOR: Record<AccessLevel, 'default' | 'info' | 'primary' | 'secondary'> = {
  NONE: 'default',
  VIEW: 'info',
  EDIT: 'primary',
  ADMIN: 'secondary',
};

/**
 * What one person can actually do — and why.
 *
 * <p>The matrix screen answers "what does this group confer"; this one answers the question an
 * administrator actually gets asked, which is "why can Anna approve refunds?". Rights arrive from several
 * groups at once and the highest wins (adr-merchant-portal-012), so the answer is never readable from a
 * single group's row. Provenance is the whole point of the screen.
 *
 * <p>It doubles as the self-service view: with no administrative permission it shows the caller their own
 * access, so someone who cannot find a button can see for themselves whether they should have it.
 */
export function EffectiveAccessPage() {
  const { t } = useTranslation('access');
  const { hasPermission } = usePermissions();
  const canInspectOthers = hasPermission('employees.manage');

  const [userId, setUserId] = useState('');
  const employees = useEmployees({ size: 100 }, { enabled: canInspectOthers });

  const mine = useMyEffectiveAccess({ enabled: !userId });
  const theirs = useEffectiveAccess(userId || undefined, { enabled: canInspectOthers });

  const query = userId ? theirs : mine;

  return (
    <Stack spacing={2}>
      {canInspectOthers ? (
        <TextField
          select
          size="small"
          label={t('effective.subjectLabel')}
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          sx={{ minWidth: 320 }}
        >
          <MenuItem value="">{t('effective.myself')}</MenuItem>
          {(employees.data?.items ?? []).map((employee) => (
            <MenuItem key={employee.id} value={employee.id}>
              {employee.name ?? employee.email}
            </MenuItem>
          ))}
        </TextField>
      ) : (
        <Alert severity="info">{t('effective.selfOnly')}</Alert>
      )}

      {query.isLoading ? (
        <LoadingState label={t('effective.loading')} />
      ) : query.isError ? (
        <ErrorState
          title={t('effective.loadError')}
          message={query.error?.message ?? ''}
          onRetry={() => void query.refetch()}
        />
      ) : (query.data?.domains ?? []).every((d) => isNoAccess(d.level)) ? (
        <EmptyState title={t('effective.empty.title')} description={t('effective.empty.description')} />
      ) : (
        <Stack spacing={1.5}>
          {(query.data?.domains ?? [])
            // A domain conferring nothing is hidden rather than listed as "No access": twenty greyed rows
            // bury the handful that matter.
            .filter((domain) => !isNoAccess(domain.level))
            .map((domain) => (
              <DomainCard key={domain.domainCode} domain={domain} />
            ))}
        </Stack>
      )}
    </Stack>
  );
}

/** One resolved domain: the winning level, where it came from, and what else contributed. */
function DomainCard({ domain }: { domain: ResolvedDomain }) {
  const { t } = useTranslation('access');

  const directGrantLabel = t('effective.directGrant');

  const exceptions = domain.capabilityDecisions.filter(
    (decision) => decision.reason === 'EXPLICIT_ALLOW' || decision.reason === 'DENIED',
  );

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Typography variant="body2" fontWeight={600} sx={{ minWidth: 200 }}>
          {t(domainLabelKey(domain.domainCode), domain.domainCode)}
        </Typography>
        <Chip size="small" color={LEVEL_COLOR[domain.level]} label={t(levelLabelKey(domain.level))} />
        <Box sx={{ flex: 1 }} />
        {domain.levelSource && (
          <Typography variant="caption" color="text.secondary">
            {t('effective.via', { source: sourceLabel(domain.levelSource, directGrantLabel) })}
          </Typography>
        )}
      </Stack>

      {(domain.contributingSources?.length ?? 0) > 0 && (
        <Tooltip title={t('effective.contributingHint')}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {t('effective.alsoFrom', {
              sources: (domain.contributingSources ?? [])
                .map((source) => sourceLabel(source, directGrantLabel))
                .join(', '),
            })}
          </Typography>
        </Tooltip>
      )}

      {exceptions.length > 0 && (
        <Stack spacing={0.5} sx={{ mt: 1 }}>
          {exceptions.map((decision) => (
            <Stack key={decision.capabilityCode} direction="row" spacing={1} alignItems="center">
              {decision.granted ? (
                <CheckCircleOutlineRoundedIcon fontSize="inherit" color="info" />
              ) : (
                <BlockRoundedIcon fontSize="inherit" color="warning" />
              )}
              <Typography variant="caption">
                {t(capabilityLabelKey(decision.capabilityCode), decision.capabilityCode)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t(reasonKey(decision.reason))}
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}
    </Paper>
  );
}

function reasonKey(reason: CapabilityReason): string {
  return `effective.reasons.${reason}`;
}

/**
 * A grant source's display name.
 *
 * Takes the already-translated fallback rather than a `t` function: a direct user grant has no group name
 * to show, and threading the i18n instance in here just to resolve one constant string buys nothing.
 */
function sourceLabel(source: GrantSource, directGrantLabel: string): string {
  return source.label ?? (source.type === 'USER' ? directGrantLabel : source.id);
}
